import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import TopBar from '../../components/TopBar';
import axiosInstance from '../../axiosInstance';
import { toast } from '../../utils/toast';
import swal from "sweetalert";
import Paystack from '../../components/Payment/Paystack';
import { 
    FaFlask, 
    FaDownload, 
    FaCalendarAlt, 
    FaFileAlt, 
    FaCheckCircle, 
    FaSpinner, 
    FaTimes, 
    FaSearch, 
    FaShoppingCart, 
    FaCheck, 
    FaPlus, 
    FaTrash, 
    FaXRay, 
    FaNotesMedical 
} from 'react-icons/fa';
import { AiFillEye } from 'react-icons/ai';

export default function LabResult() {
    const navigate = useNavigate();
    const user = JSON.parse(localStorage.getItem("userInfo") || "{}");

    // Active main tab: 'results' or 'order'
    const [activeTab, setActiveTab] = useState('results');

    // --- State for Existing Results View ---
    const [labFiles, setLabFiles] = useState([]);
    const [loadingFiles, setLoadingFiles] = useState(true);
    const [activeImageUrl, setActiveImageUrl] = useState(null);

    // --- State for Order & Pay View ---
    const [services, setServices] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loadingServices, setLoadingServices] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedTypeGroup, setSelectedTypeGroup] = useState('all'); // 'all', 'laboratory', 'radiology'
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [selectedTests, setSelectedTests] = useState([]);
    const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
    const [orderNotes, setOrderNotes] = useState('');
    const [isSubmittingOrder, setIsSubmittingOrder] = useState(false);
    const [orderModal, setOrderModal] = useState({
        isOpen: false,
        status: 'loading', // 'loading' | 'success' | 'error'
        title: '',
        message: '',
        reference: null
    });

    // 1. Fetch user's existing lab result files
    const getLabResultFiles = async (patientId) => {
        if (!patientId) return;
        try {
            setLoadingFiles(true);
            const res = await axiosInstance.get(`/lab-results/files?patient=${patientId}`);
            setLabFiles(res.data?.data?.lab_results || []);
        } catch (err) {
            console.error('Failed to load lab results:', err);
            // Ignore 404 silently for empty lists
            if (err?.response?.status !== 404) {
                toast.error(err?.response?.data?.message || 'Failed to load lab results');
            }
        } finally {
            setLoadingFiles(false);
        }
    };

    // 2. Fetch available hospital lab & radiology services
    const getAvailableServices = async () => {
        try {
            setLoadingServices(true);
            const res = await axiosInstance.get('/lab-results/services');
            if (res.data?.data) {
                setServices(res.data.data.services || []);
                setCategories(res.data.data.categories || []);
            }
        } catch (err) {
            console.error('Failed to load available tests:', err);
            toast.error(err?.response?.data?.message || 'Could not load available tests catalog');
        } finally {
            setLoadingServices(false);
        }
    };

    useEffect(() => {
        if (user?.id) {
            getLabResultFiles(user.id);
        }
    }, [user?.id]);

    useEffect(() => {
        if (activeTab === 'order' && services.length === 0) {
            getAvailableServices();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    // Handle requesting lab file via email
    const requestResult = async (id, patient, file) => {
        const data = {
            test_id: id,
            patient: patient,
            file: file,
            status: 'pending'
        };
        try {
            const response = await axiosInstance.post('/lab-results/request', data);
            toast.success(response.data.message || 'Request sent, please check your email.');
        } catch (error) {
            console.error('Error requesting result:', error);
            toast.error(error?.response?.data?.message || "An error occurred.");
        }
    };

    const handleRequestEmail = (id, patient, file) => {
        swal({
            title: "Request this lab result?",
            text: "The file will be sent to your email",
            icon: "info",
            buttons: true,
        }).then((req) => {
            if (req) {
                requestResult(id, patient, file);
            }
        });
    };

    // Filter services based on search, typeGroup, and category
    const filteredServices = useMemo(() => {
        return services.filter((svc) => {
            // Filter by type group
            if (selectedTypeGroup !== 'all' && svc.type_group !== selectedTypeGroup) {
                return false;
            }
            // Filter by category
            if (selectedCategory !== 'all' && String(svc.ser_type) !== String(selectedCategory)) {
                return false;
            }
            // Filter by search query
            if (searchQuery.trim()) {
                const q = searchQuery.toLowerCase();
                const matchName = svc.name?.toLowerCase().includes(q);
                const matchCat = svc.category_name?.toLowerCase().includes(q);
                return matchName || matchCat;
            }
            return true;
        });
    }, [services, selectedTypeGroup, selectedCategory, searchQuery]);

    // Cart calculations
    const isSelected = (id) => selectedTests.some(t => t.id === id);

    const toggleSelectTest = (test) => {
        if (isSelected(test.id)) {
            setSelectedTests(prev => prev.filter(t => t.id !== test.id));
        } else {
            setSelectedTests(prev => [...prev, test]);
        }
    };

    const removeTest = (id) => {
        setSelectedTests(prev => prev.filter(t => t.id !== id));
    };

    const totalAmount = useMemo(() => {
        return selectedTests.reduce((sum, item) => sum + (Number(item.cost) || 0), 0);
    }, [selectedTests]);

    // Handle Paystack Payment Success Callback
    const handlePaymentSuccess = async (paystackRef) => {
        const reference = paystackRef?.reference || paystackRef;
        setIsCheckoutOpen(false);
        setIsSubmittingOrder(true);
        setOrderModal({
            isOpen: true,
            status: 'loading',
            title: 'Confirming Payment...',
            message: 'Please wait while we verify your transaction with Paystack and dispatch your test requests to the hospital laboratory queue.',
            reference: reference
        });

        try {
            const payload = {
                patient: user.id,
                reference: reference,
                test_ids: selectedTests.map(t => t.id),
                location: user.location || 1,
                notes: orderNotes || 'Online Test Order'
            };

            const response = await axiosInstance.post('/lab-results/orders', payload);

            if (response.data?.success) {
                setSelectedTests([]);
                setOrderNotes('');

                // Refresh existing results list
                getLabResultFiles(user.id);

                setOrderModal({
                    isOpen: true,
                    status: 'success',
                    title: 'Order Confirmed!',
                    message: 'Your payment was successful and your test request has been dispatched to the hospital laboratory queue.',
                    reference: reference
                });
            } else {
                throw new Error(response.data?.message || 'Payment confirmed, but order could not be fulfilled');
            }
        } catch (error) {
            console.error('Error fulfilling lab order:', error);
            setOrderModal({
                isOpen: true,
                status: 'error',
                title: 'Order Reconciliation Note',
                message: error?.response?.data?.message || 'Your payment was received on Paystack. Our hospital desk will verify and process your tests shortly.',
                reference: reference
            });
        } finally {
            setIsSubmittingOrder(false);
        }
    };

    return (
        <div className='min-h-screen bg-gradient-to-br from-primary100/5 via-gray-50 to-primary100/10 pb-28'>
            <TopBar title={"Laboratory & Imaging"} url={-1} />

            <div className="px-4 py-4 max-w-2xl mx-auto">
                {/* Modern Button-Style Mode Switcher */}
                <div className="grid grid-cols-2 gap-3 mb-5">
                    <button
                        onClick={() => setActiveTab('results')}
                        className={`py-3.5 px-4 rounded-2xl font-bold text-sm flex items-center justify-center space-x-2 transition-all duration-200 active:scale-95 ${
                            activeTab === 'results'
                                ? 'bg-primary100 text-white shadow-md shadow-primary100/25 ring-2 ring-primary100'
                                : 'bg-white text-gray-700 border border-gray-200 hover:border-primary100/40 hover:bg-gray-50 shadow-sm'
                        }`}
                    >
                        <FaFlask className={`text-base ${activeTab === 'results' ? 'text-white' : 'text-primary100'}`} />
                        <span>My Lab Results</span>
                    </button>

                    <button
                        onClick={() => setActiveTab('order')}
                        className={`py-3.5 px-4 rounded-2xl font-bold text-sm flex items-center justify-center space-x-2 transition-all duration-200 active:scale-95 ${
                            activeTab === 'order'
                                ? 'bg-primary100 text-white shadow-md shadow-primary100/25 ring-2 ring-primary100'
                                : 'bg-white text-gray-700 border border-gray-200 hover:border-primary100/40 hover:bg-gray-50 shadow-sm'
                        }`}
                    >
                        <FaPlus className={`text-xs ${activeTab === 'order' ? 'text-white' : 'text-primary100'}`} />
                        <span>Book Tests</span>
                        {selectedTests.length > 0 && (
                            <span className={`text-[11px] px-2 py-0.5 rounded-full font-bold ml-1 ${
                                activeTab === 'order' ? 'bg-white text-primary100' : 'bg-primary100 text-white'
                            }`}>
                                {selectedTests.length}
                            </span>
                        )}
                    </button>
                </div>

                {/* ========================================================= */}
                {/* TAB 1: MY LAB RESULTS */}
                {/* ========================================================= */}
                {activeTab === 'results' && (
                    <div>
                        {/* Section Header */}
                        <div className="flex items-center justify-between mb-4 px-1">
                            <div>
                                <h2 className="text-base font-bold text-gray-800">
                                    Diagnostic Reports
                                </h2>
                                <p className="text-xs text-gray-500">
                                    View completed test reports and imaging files
                                </p>
                            </div>
                            {labFiles?.length > 0 && (
                                <span className="text-xs font-semibold text-gray-600 bg-gray-100 px-2.5 py-1 rounded-full">
                                    {labFiles.length} {labFiles.length === 1 ? 'Report' : 'Reports'}
                                </span>
                            )}
                        </div>

                        {/* Loading State */}
                        {loadingFiles && (
                            <div className="flex flex-col items-center justify-center py-16">
                                <div className="w-12 h-12 bg-primary100/10 rounded-full flex items-center justify-center mb-3">
                                    <FaSpinner className="text-primary100 text-2xl animate-spin" />
                                </div>
                                <p className="text-sm text-gray-500 font-medium">Loading your lab results...</p>
                            </div>
                        )}

                        {/* Lab Results List */}
                        {!loadingFiles && (
                            <div className="space-y-2.5">
                                {labFiles?.length > 0 ? (
                                    labFiles.map((test, i) => (
                                        <div key={i} className="bg-white rounded-xl shadow-xs border border-gray-100 hover:border-gray-200 transition-all p-3 sm:p-3.5">
                                            {/* Top Row: Icon + Title & Date + Unsquishable Status Badge */}
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="flex items-start space-x-2.5 min-w-0 flex-1">
                                                    <div className="w-8 h-8 bg-primary100/10 text-primary100 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
                                                        <FaFileAlt className="text-xs" />
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <h3 className="font-bold text-gray-900 text-sm leading-snug break-words">
                                                            {test?.service_name || test?.test_name || (test?.notes && test.notes !== 'N/A' ? test.notes : 'Diagnostic Report')}
                                                        </h3>
                                                        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-gray-500 mt-0.5">
                                                            <span className="flex items-center space-x-1 text-[11px] text-gray-400">
                                                                <FaCalendarAlt className="text-[9px]" />
                                                                <span>{test?.created ? test.created.substring(0, 16) : 'Recent'}</span>
                                                            </span>
                                                            {test?.doctor_notes && (
                                                                <span className="text-gray-600 italic bg-gray-100 px-1.5 py-0.5 rounded text-[10px]">
                                                                    Indication: {test.doctor_notes}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Status Badge: shrink-0 and whitespace-nowrap prevents distortion */}
                                                <div className="shrink-0 ml-1">
                                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap ${
                                                        (test?.dispensed === 'Yes' || test?.has_v2_results)
                                                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' 
                                                            : 'bg-amber-50 text-amber-700 border border-amber-200'
                                                    }`}>
                                                        {(test?.dispensed === 'Yes' || test?.has_v2_results) ? 'Result Ready' : 'Pending Lab'}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Bottom Row: File Name / V2 indicator on left, Compact Action Buttons on right */}
                                            <div className="flex items-center justify-between gap-2 pt-2 mt-2 border-t border-gray-100">
                                                <div className="text-[11px] text-gray-400 truncate min-w-0 flex-1 pr-2">
                                                    {test?.has_v2_results ? (
                                                        <span className="inline-flex items-center space-x-1 text-emerald-600 font-medium text-[11px]">
                                                            <FaCheckCircle className="text-[10px] shrink-0" />
                                                            <span>Digital Report ({test.v2_count || test.v2_results?.length || ''} parameters)</span>
                                                        </span>
                                                    ) : test?.image ? (
                                                        <span className="flex items-center space-x-1 truncate" title={test.image}>
                                                            <FaFileAlt className="text-[9px] shrink-0" />
                                                            <span className="truncate">{test.image}</span>
                                                        </span>
                                                    ) : null}
                                                </div>

                                                <div className="flex items-center space-x-1.5 shrink-0">
                                                    {(test.has_v2_results || test.dispensed === 'Yes' || test.image_url) && (
                                                        <button 
                                                            onClick={() => navigate(`/lab-report/${test.id}`)}
                                                            className="flex items-center space-x-1 bg-white border border-primary100/50 text-primary100 hover:bg-primary100/10 px-2.5 py-1 rounded-lg text-xs font-bold transition-all active:scale-95 shadow-xs"
                                                        >
                                                            <AiFillEye className="text-xs" />
                                                            <span>View Report</span>
                                                        </button>
                                                    )}
                                                    <button 
                                                        onClick={() => handleRequestEmail(test.id, test.patient, test.image || 'report')}
                                                        className="flex items-center space-x-1 bg-primary100 text-white hover:bg-primary100/90 px-2.5 py-1 rounded-lg text-xs font-semibold shadow-xs transition-all active:scale-95"
                                                    >
                                                        <FaDownload className="text-[9px]" />
                                                        <span>Email Me</span>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    /* Empty State */
                                    <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-8 text-center">
                                        <div className="w-20 h-20 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-4 text-primary100">
                                            <FaFlask className="text-3xl" />
                                        </div>
                                        <h3 className="text-lg font-bold text-gray-800 mb-1">
                                            No Lab Results Yet
                                        </h3>
                                        <p className="text-gray-500 text-xs mb-6 max-w-sm mx-auto">
                                            You don't have any completed lab results right now. You can order tests online or ask your physician.
                                        </p>
                                        <button
                                            onClick={() => setActiveTab('order')}
                                            className="bg-primary100 text-white font-semibold px-6 py-2.5 rounded-xl text-sm shadow-md hover:bg-primary100/90 transition-all"
                                        >
                                            Browse & Order Tests
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* ========================================================= */}
                {/* TAB 2: REQUEST & PAY FOR LAB & RADIOLOGY TESTS */}
                {/* ========================================================= */}
                {activeTab === 'order' && (
                    <div>
                        {/* Search and Category Filter Card */}
                        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-4 mb-4">
                            {/* Search Input */}
                            <div className="relative mb-3">
                                <FaSearch className="absolute left-3.5 top-3.5 text-gray-400 text-sm" />
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Search by test name (e.g. Malaria, Lipid, Scan)..."
                                    className="w-full pl-10 pr-9 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary100/30 focus:border-primary100 transition-all"
                                />
                                {searchQuery && (
                                    <button
                                        onClick={() => setSearchQuery('')}
                                        className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                                    >
                                        <FaTimes className="text-sm" />
                                    </button>
                                )}
                            </div>

                            {/* Main Type Toggle: All vs Lab vs Radiology */}
                            <div className="grid grid-cols-3 gap-1.5 p-1 bg-gray-100 rounded-xl mb-3">
                                <button
                                    onClick={() => { setSelectedTypeGroup('all'); setSelectedCategory('all'); }}
                                    className={`py-1.5 text-xs font-semibold rounded-lg transition-all ${
                                        selectedTypeGroup === 'all'
                                            ? 'bg-white text-gray-800 shadow-xs'
                                            : 'text-gray-500 hover:text-gray-800'
                                    }`}
                                >
                                    All ({services.length})
                                </button>
                                <button
                                    onClick={() => { setSelectedTypeGroup('laboratory'); setSelectedCategory('all'); }}
                                    className={`py-1.5 text-xs font-semibold rounded-lg transition-all flex items-center justify-center space-x-1 ${
                                        selectedTypeGroup === 'laboratory'
                                            ? 'bg-white text-primary100 shadow-xs'
                                            : 'text-gray-500 hover:text-gray-800'
                                    }`}
                                >
                                    <FaFlask className="text-[10px]" />
                                    <span>Laboratory</span>
                                </button>
                                <button
                                    onClick={() => { setSelectedTypeGroup('radiology'); setSelectedCategory('all'); }}
                                    className={`py-1.5 text-xs font-semibold rounded-lg transition-all flex items-center justify-center space-x-1 ${
                                        selectedTypeGroup === 'radiology'
                                            ? 'bg-white text-indigo-700 shadow-xs'
                                            : 'text-gray-500 hover:text-gray-800'
                                    }`}
                                >
                                    <FaXRay className="text-[10px]" />
                                    <span>Radiology</span>
                                </button>
                            </div>

                            {/* Category Filter Chips */}
                            {categories.length > 0 && (
                                <div className="flex space-x-2 overflow-x-auto pb-1 scrollbar-none">
                                    <button
                                        onClick={() => setSelectedCategory('all')}
                                        className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                                            selectedCategory === 'all'
                                                ? 'bg-primary100 text-white shadow-xs'
                                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                        }`}
                                    >
                                        All Categories
                                    </button>
                                    {categories
                                        .filter(cat => selectedTypeGroup === 'all' || cat.type_group === selectedTypeGroup)
                                        .map(cat => (
                                            <button
                                                key={cat.id}
                                                onClick={() => setSelectedCategory(String(cat.id))}
                                                className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                                                    selectedCategory === String(cat.id)
                                                        ? 'bg-primary100 text-white shadow-xs'
                                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                }`}
                                            >
                                                {cat.name} ({cat.count})
                                            </button>
                                        ))}
                                </div>
                            )}
                        </div>

                        {/* Loading State */}
                        {loadingServices && (
                            <div className="flex flex-col items-center justify-center py-16">
                                <div className="w-12 h-12 bg-primary100/10 rounded-full flex items-center justify-center mb-3">
                                    <FaSpinner className="text-primary100 text-2xl animate-spin" />
                                </div>
                                <p className="text-sm text-gray-500 font-medium">Loading hospital services catalog...</p>
                            </div>
                        )}

                        {/* Test Selection Cards List */}
                        {!loadingServices && (
                            <div className="space-y-3">
                                {filteredServices.length > 0 ? (
                                    filteredServices.map((service) => {
                                        const selected = isSelected(service.id);
                                        const isRad = service.type_group === 'radiology';

                                        return (
                                            <div
                                                key={service.id}
                                                onClick={() => toggleSelectTest(service)}
                                                className={`p-4 rounded-2xl cursor-pointer transition-all duration-200 border ${
                                                    selected
                                                        ? 'bg-emerald-50/70 border-primary100 shadow-md ring-1 ring-primary100'
                                                        : 'bg-white border-gray-100 hover:border-gray-200 hover:shadow-sm'
                                                }`}
                                            >
                                                <div className="flex items-start justify-between">
                                                    <div className="flex-1 pr-3">
                                                        <div className="flex items-center space-x-2 mb-1.5">
                                                            <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider ${
                                                                isRad 
                                                                    ? 'bg-indigo-50 text-indigo-700 border border-indigo-100'
                                                                    : 'bg-emerald-50 text-emerald-700 border border-emerald-100'
                                                            }`}>
                                                                {service.category_name || (isRad ? 'Radiology' : 'Lab')}
                                                            </span>
                                                        </div>

                                                        <h4 className="font-semibold text-gray-900 text-sm leading-snug">
                                                            {service.name}
                                                        </h4>

                                                        <p className="text-base font-extrabold text-primary100 mt-2">
                                                            ₦{Number(service.cost).toLocaleString()}
                                                        </p>
                                                    </div>

                                                    {/* Selection Indicator */}
                                                    <div className={`w-8 h-8 rounded-xl flex items-center justify-center transition-all ${
                                                        selected
                                                            ? 'bg-primary100 text-white shadow-sm'
                                                            : 'bg-gray-100 text-gray-400 border border-gray-200'
                                                    }`}>
                                                        {selected ? (
                                                            <FaCheck className="text-xs" />
                                                        ) : (
                                                            <FaPlus className="text-xs" />
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div className="bg-white rounded-3xl p-8 text-center border border-gray-100">
                                        <FaSearch className="text-gray-300 text-3xl mx-auto mb-3" />
                                        <h4 className="font-semibold text-gray-700">No tests match your filter</h4>
                                        <p className="text-xs text-gray-400 mt-1">Try modifying your search or clearing category filters</p>
                                        <button
                                            onClick={() => { setSearchQuery(''); setSelectedTypeGroup('all'); setSelectedCategory('all'); }}
                                            className="mt-3 text-xs text-primary100 font-bold"
                                        >
                                            Reset Filters
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* ========================================================= */}
                {/* FLOATING CART SUMMARY (WHEN TESTS SELECTED) */}
                {/* ========================================================= */}
                {activeTab === 'order' && selectedTests.length > 0 && (
                    <div className="fixed bottom-4 left-4 right-4 max-w-2xl mx-auto z-40">
                        <div className="bg-primary100 text-white rounded-2xl p-4 shadow-2xl flex items-center justify-between border border-white/20 backdrop-blur-md">
                            <div>
                                <div className="flex items-center space-x-2">
                                    <span className="bg-white/20 text-white text-xs px-2 py-0.5 rounded-full font-bold">
                                        {selectedTests.length} {selectedTests.length === 1 ? 'Test' : 'Tests'}
                                    </span>
                                    <span className="text-xs text-white/80">Selected</span>
                                </div>
                                <div className="text-lg font-extrabold mt-0.5">
                                    ₦{totalAmount.toLocaleString()}
                                </div>
                            </div>

                            <button
                                onClick={() => setIsCheckoutOpen(true)}
                                className="bg-white text-primary100 px-5 py-2.5 rounded-xl font-bold text-sm shadow-md hover:bg-gray-50 active:scale-95 transition-all flex items-center space-x-2"
                            >
                                <FaShoppingCart className="text-xs" />
                                <span>Proceed to Pay</span>
                            </button>
                        </div>
                    </div>
                )}

                {/* ========================================================= */}
                {/* CHECKOUT & PAYMENT MODAL */}
                {/* ========================================================= */}
                {isCheckoutOpen && (
                    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
                        <div 
                            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                            onClick={() => setIsCheckoutOpen(false)}
                        ></div>

                        <div className="relative bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden z-10 animate-in slide-in-from-bottom duration-300">
                            {/* Modal Header */}
                            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                                <div className="flex items-center space-x-2">
                                    <FaNotesMedical className="text-primary100 text-lg" />
                                    <h3 className="font-bold text-gray-900 text-base">Order Review & Payment</h3>
                                </div>
                                <button
                                    onClick={() => setIsCheckoutOpen(false)}
                                    className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200"
                                >
                                    <FaTimes className="text-xs" />
                                </button>
                            </div>

                            {/* Modal Body */}
                            <div className="p-6 overflow-y-auto space-y-4">
                                <div>
                                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                                        Selected Services ({selectedTests.length})
                                    </h4>
                                    <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
                                        {selectedTests.map(test => (
                                            <div key={test.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-100">
                                                <div className="flex-1 pr-2">
                                                    <p className="text-xs font-semibold text-gray-800 leading-snug">{test.name}</p>
                                                    <span className="text-[10px] text-gray-500 uppercase">{test.category_name}</span>
                                                </div>
                                                <div className="flex items-center space-x-3">
                                                    <span className="text-xs font-extrabold text-primary100">₦{Number(test.cost).toLocaleString()}</span>
                                                    <button
                                                        onClick={() => removeTest(test.id)}
                                                        className="text-gray-400 hover:text-red-500 p-1"
                                                    >
                                                        <FaTrash className="text-[11px]" />
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Patient Instructions / Notes */}
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 mb-1">
                                        Clinical / Patient Notes (Optional)
                                    </label>
                                    <textarea
                                        rows={2}
                                        value={orderNotes}
                                        onChange={(e) => setOrderNotes(e.target.value)}
                                        placeholder="Add any specific physician instructions or notes..."
                                        className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary100/30"
                                    ></textarea>
                                </div>

                                {/* Summary Box */}
                                <div className="bg-primary100/5 rounded-2xl p-4 border border-primary100/20">
                                    <div className="flex justify-between text-xs text-gray-600 mb-1.5">
                                        <span>Subtotal ({selectedTests.length} tests)</span>
                                        <span>₦{totalAmount.toLocaleString()}</span>
                                    </div>
                                    <div className="flex justify-between text-sm font-extrabold text-gray-900 pt-2 border-t border-primary100/20">
                                        <span>Total Due</span>
                                        <span className="text-primary100">₦{totalAmount.toLocaleString()}</span>
                                    </div>
                                </div>

                                {/* Notice */}
                                <div className="flex items-start space-x-2 text-[11px] text-gray-500 bg-gray-50 p-3 rounded-xl border border-gray-200/60">
                                    <FaCheckCircle className="text-emerald-500 text-xs mt-0.5 shrink-0" />
                                    <p>Upon payment, your tests will be entered into the hospital EMR system ready for specimen collection in Phlebotomy/Radiology.</p>
                                </div>
                            </div>

                            {/* Modal Footer with Paystack Checkout */}
                            <div className="p-6 bg-gray-50 border-t border-gray-100">
                                {isSubmittingOrder ? (
                                    <div className="flex items-center justify-center py-3 space-x-2 text-primary100 font-semibold text-sm">
                                        <FaSpinner className="animate-spin text-base" />
                                        <span>Fulfilling lab order in hospital system...</span>
                                    </div>
                                ) : (
                                    <Paystack
                                        amount={totalAmount}
                                        email={user.email || 'patient@misas.ng'}
                                        phone={user.mobile || ''}
                                        notes={`Online Lab Order - ${selectedTests.length} tests`}
                                        paymentType="lab_order"
                                        compData={{ test_ids: selectedTests.map(t => t.id) }}
                                        buttonText={`Pay ₦${totalAmount.toLocaleString()} Now`}
                                        buttonClassName="w-full bg-primary100 hover:bg-primary100/90 text-white font-bold py-3.5 px-6 rounded-xl shadow-lg transition-all text-sm"
                                        onPaymentSuccess={handlePaymentSuccess}
                                        onPaymentError={(err) => toast.error(err || 'Payment cancelled or failed')}
                                        channels={['card', 'bank', 'bank_transfer', 'ussd', 'qr']}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* ========================================================= */}
                {/* ORDER STATUS / LOADING & CONFIRMATION MODAL */}
                {/* ========================================================= */}
                {orderModal.isOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm transition-all duration-300">
                        <div className="relative bg-white rounded-3xl shadow-2xl max-w-sm w-full p-6 text-center border border-gray-100 transform transition-all duration-300 scale-100">
                            {/* Top Close 'X' button for success / error states */}
                            {orderModal.status !== 'loading' && (
                                <button
                                    onClick={() => setOrderModal(prev => ({ ...prev, isOpen: false }))}
                                    className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 flex items-center justify-center transition-colors"
                                    aria-label="Close modal"
                                >
                                    <FaTimes className="text-xs" />
                                </button>
                            )}

                            {/* STATUS: LOADING */}
                            {orderModal.status === 'loading' && (
                                <div className="py-6 flex flex-col items-center">
                                    <div className="relative mb-5 flex items-center justify-center">
                                        <div className="w-20 h-20 rounded-full border-4 border-primary100/20 border-t-primary100 animate-spin"></div>
                                        <div className="absolute inset-0 flex items-center justify-center">
                                            <FaFlask className="text-primary100 text-2xl animate-pulse" />
                                        </div>
                                    </div>
                                    <h3 className="text-xl font-extrabold text-gray-900 mb-2">
                                        {orderModal.title}
                                    </h3>
                                    <p className="text-xs text-gray-500 mb-4 leading-relaxed max-w-xs">
                                        {orderModal.message}
                                    </p>
                                    {orderModal.reference && (
                                        <div className="bg-gray-50 border border-gray-200/80 rounded-xl px-3 py-1.5 text-[11px] text-gray-600 font-mono">
                                            Ref: <span className="font-semibold text-gray-800">{orderModal.reference}</span>
                                        </div>
                                    )}
                                    <div className="mt-5 flex items-center space-x-1.5 text-[11px] text-primary100 font-semibold bg-primary100/5 px-3 py-1.5 rounded-full">
                                        <FaSpinner className="animate-spin text-xs" />
                                        <span>Communicating with hospital EMR...</span>
                                    </div>
                                </div>
                            )}

                            {/* STATUS: SUCCESS */}
                            {orderModal.status === 'success' && (
                                <div className="py-4 flex flex-col items-center">
                                    <div className="w-20 h-20 rounded-full bg-emerald-50 border-4 border-emerald-100 flex items-center justify-center mb-5 shadow-sm">
                                        <div className="w-12 h-12 rounded-full bg-emerald-500 flex items-center justify-center shadow-md shadow-emerald-500/30">
                                            <FaCheck className="text-white text-2xl stroke-current" />
                                        </div>
                                    </div>
                                    <h3 className="text-2xl font-black text-gray-900 mb-2">
                                        {orderModal.title}
                                    </h3>
                                    <p className="text-xs text-gray-600 mb-4 leading-relaxed">
                                        {orderModal.message}
                                    </p>

                                    {orderModal.reference && (
                                        <div className="mb-6 w-full bg-gray-50 border border-gray-200 rounded-xl p-2.5 text-xs text-gray-600 flex justify-between items-center">
                                            <span className="text-gray-400 font-medium">Ref No:</span>
                                            <span className="font-mono font-bold text-gray-800">{orderModal.reference}</span>
                                        </div>
                                    )}

                                    <div className="w-full space-y-2.5">
                                        <button
                                            onClick={() => {
                                                setOrderModal(prev => ({ ...prev, isOpen: false }));
                                                setActiveTab('results');
                                            }}
                                            className="w-full bg-primary100 hover:bg-primary100/90 text-white font-bold py-3.5 px-5 rounded-2xl shadow-lg shadow-primary100/20 transition-all text-sm flex items-center justify-center space-x-2 active:scale-98"
                                        >
                                            <FaFlask className="text-xs" />
                                            <span>View My Lab Results</span>
                                        </button>

                                        <button
                                            onClick={() => setOrderModal(prev => ({ ...prev, isOpen: false }))}
                                            className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold py-3 px-5 rounded-2xl transition-all text-sm active:scale-98"
                                        >
                                            Okay / Close
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* STATUS: ERROR */}
                            {orderModal.status === 'error' && (
                                <div className="py-4 flex flex-col items-center">
                                    <div className="w-20 h-20 rounded-full bg-amber-50 border-4 border-amber-100 flex items-center justify-center mb-5 shadow-sm">
                                        <div className="w-12 h-12 rounded-full bg-amber-500 flex items-center justify-center shadow-md shadow-amber-500/30">
                                            <FaTimes className="text-white text-2xl" />
                                        </div>
                                    </div>
                                    <h3 className="text-xl font-black text-gray-900 mb-2">
                                        {orderModal.title}
                                    </h3>
                                    <p className="text-xs text-gray-600 mb-4 leading-relaxed">
                                        {orderModal.message}
                                    </p>

                                    {orderModal.reference && (
                                        <div className="mb-6 w-full bg-gray-50 border border-gray-200 rounded-xl p-2.5 text-xs text-gray-600 flex justify-between items-center">
                                            <span className="text-gray-400 font-medium">Payment Ref:</span>
                                            <span className="font-mono font-bold text-gray-800">{orderModal.reference}</span>
                                        </div>
                                    )}

                                    <button
                                        onClick={() => setOrderModal(prev => ({ ...prev, isOpen: false }))}
                                        className="w-full bg-gray-800 hover:bg-gray-900 text-white font-bold py-3.5 px-5 rounded-2xl transition-all text-sm"
                                    >
                                        Okay, Close
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* ========================================================= */}
                {/* MODAL FOR VIEWING REPORT IMAGE */}
                {/* ========================================================= */}
                {activeImageUrl && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <div 
                            className="absolute inset-0 bg-black/70 backdrop-blur-md"
                            onClick={() => setActiveImageUrl(null)}
                        ></div>

                        <div className="relative bg-white rounded-3xl shadow-2xl max-w-3xl w-full overflow-hidden flex flex-col max-h-[85vh] border border-gray-100 z-10">
                            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                                <div>
                                    <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                                        <FaFileAlt className="text-primary100" />
                                        Lab Result Preview
                                    </h3>
                                    <p className="text-xs text-gray-500">Laboratory Diagnostic Report File</p>
                                </div>
                                <button 
                                    onClick={() => setActiveImageUrl(null)}
                                    className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200"
                                >
                                    <FaTimes className="text-xs" />
                                </button>
                            </div>

                            <div className="p-6 bg-gray-50 overflow-y-auto flex-1 flex items-center justify-center min-h-[250px]">
                                <img 
                                    src={activeImageUrl} 
                                    alt="Laboratory Result" 
                                    className="max-h-[55vh] object-contain rounded-xl shadow-md bg-white border border-gray-200"
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = "https://via.placeholder.com/600x400?text=Report+Image+Unavailable";
                                    }}
                                />
                            </div>

                            <div className="px-6 py-3.5 bg-gray-50 border-t border-gray-100 flex justify-between items-center">
                                <a 
                                    href={activeImageUrl} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="flex items-center space-x-1.5 bg-primary100 text-white px-4 py-2 rounded-xl font-semibold text-xs shadow-sm hover:bg-primary100/90"
                                >
                                    <FaDownload className="text-xs" />
                                    <span>Download</span>
                                </a>
                                <button 
                                    onClick={() => setActiveImageUrl(null)}
                                    className="bg-white border border-gray-200 text-gray-700 px-4 py-2 rounded-xl font-semibold text-xs hover:bg-gray-100"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
